"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";

interface LinkedAccount {
	type: string;
	address?: string;
	walletAddress?: string;
	email?: string;
	username?: string;
	name?: string;
	walletClientType?: string;
}

interface SocialAccountsOnboardingProps {
	linkedAccounts: LinkedAccount[];
	isMobile: boolean;
	hasPhantom: boolean;
	isSyncing: boolean;
	linkEmail: () => void;
	linkPhone: () => void;
	linkTwitter: () => void;
	linkGoogle: () => void;
	linkDiscord: () => void;
	linkPhantom: () => void;
	onDismiss: () => void;
}

interface OptionCard {
	id: string;
	name: string;
	description: string;
	borderColor: string;
	bgColor: string;
	textColor: string;
	icon: React.ReactNode;
	onLink: () => void;
	show: boolean;
	isLinked: boolean;
}

function CheckIcon() {
	return (
		<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
			<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
		</svg>
	);
}

export const SocialAccountsOnboarding: React.FC<SocialAccountsOnboardingProps> = ({
	linkedAccounts,
	isMobile,
	hasPhantom,
	isSyncing,
	linkEmail,
	linkPhone,
	linkTwitter,
	linkGoogle,
	linkDiscord,
	linkPhantom,
	onDismiss,
}) => {
	const [visible, setVisible] = useState(false);
	const dialogRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const t = setTimeout(() => setVisible(true), 30);
		return () => clearTimeout(t);
	}, []);

	const handleDismiss = useCallback(() => {
		setVisible(false);
		setTimeout(onDismiss, 250);
	}, [onDismiss]);

	useEffect(() => {
		if (!visible) return;
		dialogRef.current?.focus();
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				handleDismiss();
			}
		};
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [visible, handleDismiss]);

	const hasType = (type: string) => linkedAccounts.some((a) => a.type === type);

	const hasExternalWallet = linkedAccounts.some(
		(a) =>
			a.type === "wallet" &&
			a.walletClientType !== "privy" &&
			a.walletClientType !== "smart_wallet"
	);

	const options: OptionCard[] = [
		{
			id: "email",
			name: "Email",
			description: "Add an email for account recovery",
			borderColor: "border-gray-500/30",
			bgColor: "bg-gray-500/10",
			textColor: "text-gray-300",
			icon: (
				<svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
					<path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
				</svg>
			),
			onLink: linkEmail,
			show: true,
			isLinked: hasType("email"),
		},
		{
			id: "phone",
			name: "Phone Number",
			description: "Verify your identity with SMS",
			borderColor: "border-emerald-500/30",
			bgColor: "bg-emerald-500/10",
			textColor: "text-emerald-400",
			icon: (
				<svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
					<path strokeLinecap="round" strokeLinejoin="round" d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.7A2 2 0 012.18 1H5.1a2 2 0 012 1.72 12.6 12.6 0 00.7 2.81 2 2 0 01-.45 2.11L6.11 8.8a16 16 0 006.72 6.72l1.16-1.16a2 2 0 012.11-.45 12.6 12.6 0 002.81.7A2 2 0 0122 16.92z" />
				</svg>
			),
			onLink: linkPhone,
			show: true,
			isLinked: hasType("phone"),
		},
		{
			id: "twitter_oauth",
			name: "Twitter / X",
			description: "Show your handle & earn a verified badge",
			borderColor: "border-sky-500/30",
			bgColor: "bg-sky-500/10",
			textColor: "text-sky-400",
			icon: (
				<svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
					<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.259 5.63 5.905-5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
				</svg>
			),
			onLink: linkTwitter,
			show: true,
			isLinked: hasType("twitter_oauth"),
		},
		{
			id: "google_oauth",
			name: "Google",
			description: "Use your Google identity & profile name",
			borderColor: "border-blue-500/30",
			bgColor: "bg-blue-500/10",
			textColor: "text-blue-400",
			icon: (
				<svg className="w-6 h-6" viewBox="0 0 24 24">
					<path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
					<path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
					<path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
					<path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
				</svg>
			),
			onLink: linkGoogle,
			show: true,
			isLinked: hasType("google_oauth"),
		},
		{
			id: "discord_oauth",
			name: "Discord",
			description: "Connect your community & Discord username",
			borderColor: "border-indigo-500/30",
			bgColor: "bg-indigo-500/10",
			textColor: "text-indigo-400",
			icon: (
				<svg className="w-6 h-6" viewBox="0 0 24 24" fill="#5865F2">
					<path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.04.036.05a19.863 19.863 0 0 0 5.993 3.03.077.077 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
				</svg>
			),
			onLink: linkDiscord,
			show: true,
			isLinked: hasType("discord_oauth"),
		},
		{
			id: "phantom",
			name: "Phantom Wallet",
			description: "Link your external Solana wallet",
			borderColor: "border-purple-500/30",
			bgColor: "bg-purple-500/10",
			textColor: "text-purple-400",
			icon: (
				<svg className="w-6 h-6" viewBox="0 0 128 128" fill="none">
					<rect width="128" height="128" rx="24" fill="#AB9FF2" />
					<path
						d="M110.584 64.905c0 25.65-19.365 46.222-43.235 46.222-23.87 0-37.177-16.108-37.177-16.108s-5.148 8.55-14.862 8.55C8.628 103.569 4 98.759 4 92.8c0-5.96 4.627-10.77 11.31-10.77 9.714 0 14.862 8.55 14.862 8.55S16.87 72.18 16.87 55.29c0-23.87 17.307-43.235 38.666-43.235 7.594 0 14.665 2.46 20.527 6.654 2.895-4.033 7.595-6.654 12.895-6.654 8.868 0 16.05 7.183 16.05 16.05 0 3.455-1.094 6.655-2.95 9.274 5.392 6.655 8.526 15.382 8.526 27.526z"
						fill="white"
					/>
					<ellipse cx="52.5" cy="59" rx="7.5" ry="7.5" fill="#AB9FF2" />
					<ellipse cx="76.5" cy="59" rx="7.5" ry="7.5" fill="#AB9FF2" />
				</svg>
			),
			onLink: linkPhantom,
			show: !isMobile && hasPhantom && !hasExternalWallet,
			isLinked: hasExternalWallet,
		},
	];

	const visibleOptions = options.filter((o) => o.show || o.isLinked);
	const linkedCount = visibleOptions.filter((o) => o.isLinked).length;

	return (
		<div
			className={`fixed inset-0 z-50 flex items-center justify-center px-4 py-6 transition-all duration-250 ${
				visible ? "opacity-100" : "opacity-0"
			}`}
		>
			{/* Backdrop */}
			<div
				className="absolute inset-0 bg-black/80 backdrop-blur-sm"
				onClick={handleDismiss}
			/>

			{/* Modal */}
			<div
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby="social-onboarding-title"
				tabIndex={-1}
				className={`relative z-10 w-full max-w-lg bg-[#141414] border border-white/10 rounded-2xl shadow-2xl flex flex-col max-h-[90vh] transition-all duration-250 ${
					visible ? "scale-100 translate-y-0" : "scale-95 translate-y-4"
				}`}
			>
				{/* Top accent bar */}
				<div className="h-1 w-full bg-gradient-to-r from-mgt-green via-mgt-yellow to-mgt-green rounded-t-2xl flex-shrink-0" />

				{/* Scrollable content */}
				<div className="overflow-y-auto flex-1 p-6 space-y-5">
					{/* Header */}
					<div className="text-center space-y-2">
						<div className="flex items-center justify-center mb-3">
							<div className="w-14 h-14 rounded-2xl bg-mgt-green/10 border border-mgt-green/20 flex items-center justify-center">
								<svg className="w-7 h-7 text-mgt-green" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
									<path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
								</svg>
							</div>
						</div>
						<h2 id="social-onboarding-title" className="text-2xl font-bold text-white">Welcome to MyGeoTokens!</h2>
						<p className="text-gray-400 text-sm leading-relaxed">
							Link your accounts to get a verified badge, secure recovery options, and unlock the best of the platform.
						</p>
					</div>

					{/* Progress pill */}
					{linkedCount > 0 && (
						<div className="flex items-center gap-2 bg-mgt-green/10 border border-mgt-green/20 rounded-lg px-3 py-2">
							<svg className="w-4 h-4 text-mgt-green flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
								<path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
							<span className="text-mgt-green text-xs font-medium">
								{linkedCount} account{linkedCount !== 1 ? "s" : ""} linked
							</span>
						</div>
					)}

					{/* Options list */}
					<div className="space-y-2.5">
						{visibleOptions.map((opt) => (
							<div
								key={opt.id}
								className={`rounded-xl px-4 py-3 border flex items-center justify-between gap-3 transition-colors ${
									opt.isLinked
										? `${opt.bgColor} ${opt.borderColor}`
										: "bg-[#1A1A1A] border-white/10 hover:border-white/20"
								}`}
							>
								{/* Icon + text */}
								<div className="flex items-center gap-3 min-w-0">
									<div
										className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
											opt.isLinked ? opt.bgColor : "bg-white/5"
										}`}
									>
										<span className={opt.isLinked ? opt.textColor : "text-gray-400"}>
											{opt.icon}
										</span>
									</div>
									<div className="min-w-0">
										<p className={`text-sm font-semibold truncate ${opt.isLinked ? opt.textColor : "text-white"}`}>
											{opt.name}
										</p>
										<p className="text-xs text-gray-500 truncate">{opt.description}</p>
									</div>
								</div>

								{/* Action */}
								{opt.isLinked ? (
									<div className={`flex items-center gap-1 text-xs font-medium flex-shrink-0 ${opt.textColor}`}>
										<CheckIcon />
										<span>Linked</span>
									</div>
								) : (
									<button
										onClick={opt.onLink}
										disabled={isSyncing}
										className={`text-xs font-semibold px-4 py-1.5 rounded-lg border flex-shrink-0 transition-colors disabled:opacity-40 ${opt.bgColor} ${opt.textColor} ${opt.borderColor} hover:opacity-80`}
									>
										Link
									</button>
								)}
							</div>
						))}
					</div>
				</div>

				{/* Sticky footer */}
				<div className="flex-shrink-0 px-6 pb-6 pt-2 space-y-3">
					{linkedCount >= 1 && (
						<button
							onClick={handleDismiss}
							className="w-full bg-mgt-green hover:bg-mgt-green/90 transition-colors text-black font-semibold py-3 rounded-xl text-sm"
						>
							Continue to Profile
						</button>
					)}
					<button
						onClick={handleDismiss}
						className="w-full text-center text-gray-500 hover:text-gray-300 text-sm transition-colors py-1"
					>
						{linkedCount >= 1 ? "I'll do this later" : "Skip for now →"}
					</button>
				</div>
			</div>
		</div>
	);
};
